import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Check, Loader2, MapPin, CreditCard, Send } from 'lucide-react';
import axios from 'axios';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { BookingMap } from '../components/BookingMap';
import { Calendar as DatePicker } from '../components/ui/calendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceTypes = [
  { id: 'consultation', label: 'New Theater Consultation' },
  { id: 'maintenance', label: 'Maintenance Visit' },
  { id: 'cleaning', label: 'Theater Cleaning' },
  { id: 'upgrade', label: 'System Upgrade' },
  { id: 'other', label: 'Other' },
];

const timeGroups = [
  { label: 'Morning', slots: ['8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM'] },
  { label: 'Afternoon', slots: ['12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'] },
  { label: 'Evening', slots: ['5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM'] },
];

export default function BookingPage() {
  const [formData, setFormData] = useState({
    full_name: '', email: '', phone: '', address: '',
    preferred_date: '', preferred_time: '',
    service_type: 'consultation', budget: '', message: '',
  });
  const [submitted, setSubmitted] = useState(null); // 'custom' | 'standard'
  const [loading, setLoading] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [regions, setRegions] = useState(null);
  const [regionOverride, setRegionOverride] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    if (formData.preferred_date) {
      axios.get(`${API}/availability?date=${formData.preferred_date}`)
        .then(r => setBookedSlots(r.data.booked_slots || []))
        .catch(() => setBookedSlots([]));
    }
  }, [formData.preferred_date]);

  // Distance-based fee quote when address changes
  useEffect(() => {
    if (regionOverride) return;
    clearTimeout(debounceRef.current);
    if (!formData.address || formData.address.trim().length < 8) {
      setQuote(null);
      setRegions(null);
      return;
    }
    setQuoteLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/bookings/quote`, { address: formData.address });
        if (res.data.geocode_failed) {
          setQuote(null);
          setRegions(res.data.regions || []);
        } else {
          setQuote(res.data);
          setRegions(null);
        }
      } catch {
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 900);
    return () => clearTimeout(debounceRef.current);
  }, [formData.address, regionOverride]);

  const selectRegion = async (region) => {
    setRegionOverride(region);
    if (!region) { setQuote(null); return; }
    setQuoteLoading(true);
    try {
      const res = await axios.post(`${API}/bookings/quote`, { region });
      setQuote(res.data);
    } catch {
      setQuote(null);
    } finally {
      setQuoteLoading(false);
    }
  };

  const isComplete = formData.full_name.trim() && formData.email.trim() && formData.phone.trim()
    && formData.address.trim() && formData.preferred_date && formData.preferred_time;
  const hasQuote = quote && (quote.fee || quote.custom_quote);
  const canSubmit = isComplete && hasQuote && !loading && !quoteLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        ...formData,
        flow: 'travel_fee',
        region_override: regionOverride || '',
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setSubmitted(res.data.custom_quote ? 'custom' : 'standard');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const minDate = new Date(); minDate.setDate(minDate.getDate() + 1); minDate.setHours(0, 0, 0, 0);
  const maxDate = new Date(); maxDate.setDate(maxDate.getDate() + 90);
  const selectedDate = formData.preferred_date ? new Date(formData.preferred_date + 'T12:00:00') : undefined;

  return (
    <div className="min-h-screen pt-24" data-testid="booking-page">
      {/* Hero */}
      <section className="py-20 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">Book a Site Survey</p>
            <h1 className="font-playfair text-4xl lg:text-5xl text-white mb-4">
              Let's Bring Your <span className="text-[#D4AF37]">Vision to Life</span>
            </h1>
            <p className="text-white/40 font-manrope text-lg max-w-2xl leading-relaxed">
              Book your on-site consultation today. Your travel fee is calculated automatically from your address — pay securely online and receive instant confirmation with your exclusive client activation code.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#D4AF37]/5 border border-[#D4AF37] rounded-sm p-12 text-center"
              data-testid="booking-success"
            >
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={32} className="text-black" />
              </div>
              <h2 className="font-playfair text-2xl text-white mb-3">
                {submitted === 'custom' ? 'Custom Quote Requested!' : 'Consultation Booked!'}
              </h2>
              <p className="text-white/50 font-manrope text-sm max-w-md mx-auto">
                {submitted === 'custom'
                  ? "Your location is beyond our standard service area. Our team will contact you within 24 hours with a personalized travel quote. A confirmation email is on its way."
                  : 'Thank you for booking with Dream Room Theaters. Our team will confirm your appointment and reach out with next steps.'}
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleSubmit}
              className="space-y-8"
              data-testid="booking-form"
            >
              {/* Service Type */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-3 block">Service Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="service-type-options">
                  {serviceTypes.map(st => (
                    <button
                      key={st.id}
                      type="button"
                      onClick={() => update('service_type', st.id)}
                      className={`
                        px-4 py-3 rounded-sm font-manrope text-xs uppercase tracking-widest transition-all duration-300
                        ${formData.service_type === st.id
                          ? 'bg-[#D4AF37] text-black font-bold'
                          : 'bg-[#1a1a1a] border border-white/10 text-white/50 hover:border-[#D4AF37]/30'
                        }
                      `}
                      data-testid={`service-${st.id}`}
                    >
                      {st.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Full Name</label>
                  <input
                    type="text" required
                    value={formData.full_name}
                    onChange={e => update('full_name', e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors"
                    placeholder="John Smith"
                    data-testid="booking-name-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Email</label>
                  <input
                    type="email" required
                    value={formData.email}
                    onChange={e => update('email', e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors"
                    placeholder="john@example.com"
                    data-testid="booking-email-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Phone</label>
                  <input
                    type="tel" required
                    value={formData.phone}
                    onChange={e => update('phone', e.target.value)}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors"
                    placeholder="(416) 555-0123"
                    data-testid="booking-phone-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">
                    <MapPin size={12} className="inline mr-2" />Project Address
                  </label>
                  <AddressAutocomplete
                    value={formData.address}
                    onChange={val => { setRegionOverride(''); update('address', val); }}
                    placeholder="Start typing your address..."
                    className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors"
                    dataTestId="booking-address-input"
                  />
                </div>
              </div>

              {/* Project Budget */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Project Budget</label>
                <select
                  value={formData.budget}
                  onChange={e => update('budget', e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-4 py-3 text-white font-manrope text-sm rounded-sm"
                  data-testid="booking-budget-select"
                >
                  <option value="">Select your budget range (optional)...</option>
                  <option value="Under $50,000">Under $50,000</option>
                  <option value="$50,000 – $100,000">$50,000 – $100,000</option>
                  <option value="$100,000 – $150,000">$100,000 – $150,000</option>
                  <option value="$150,000+">$150,000+</option>
                </select>
              </div>

              {/* Region fallback when geocoding fails */}
              {regions && !quote && (
                <div data-testid="region-fallback">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">
                    We couldn't locate that address — select your region
                  </label>
                  <select
                    value={regionOverride}
                    onChange={e => selectRegion(e.target.value)}
                    className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-4 py-3 text-white font-manrope text-sm rounded-sm"
                    data-testid="region-select"
                  >
                    <option value="">Choose your region...</option>
                    {regions.map(r => (
                      <option key={r} value={r}>{r === 'custom' ? 'Beyond 1,000 km — Custom Quote' : r}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Map + fee */}
              {(quote?.lat != null || quoteLoading) && (
                <div className="rounded-sm overflow-hidden border border-white/10 relative h-56" data-testid="booking-map-wrap">
                  {quote?.lat != null && <BookingMap lat={quote.lat} lng={quote.lng} />}
                  {quoteLoading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[500]">
                      <Loader2 size={22} className="text-[#D4AF37] animate-spin" />
                    </div>
                  )}
                </div>
              )}

              {/* Date & Time Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-3 block">
                    <Calendar size={12} className="inline mr-2" />Preferred Date
                  </label>
                  <div className="inline-block bg-[#16161a] border border-white/10 rounded-sm" data-testid="date-calendar">
                    <DatePicker
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => { if (d) update('preferred_date', fmtDate(d)); }}
                      disabled={[{ before: minDate }, { after: maxDate }, { dayOfWeek: [0] }]}
                      defaultMonth={selectedDate || minDate}
                      classNames={{
                        caption_label: 'text-white font-playfair text-base',
                        nav_button: 'h-7 w-7 bg-transparent border border-white/10 text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 rounded-sm p-0 inline-flex items-center justify-center transition-colors',
                        head_cell: 'text-white/25 font-manrope text-[10px] uppercase tracking-widest w-10 font-normal',
                        cell: 'text-center p-0.5',
                        day: 'h-9 w-9 font-manrope text-sm text-white/70 hover:bg-[#D4AF37]/20 hover:text-white rounded-sm transition-colors inline-flex items-center justify-center',
                        day_selected: 'bg-[#D4AF37] text-black font-bold hover:bg-[#D4AF37] hover:text-black',
                        day_disabled: 'text-white/10 hover:bg-transparent cursor-not-allowed',
                        day_outside: 'text-white/10',
                        day_today: 'border border-[#D4AF37]/40',
                      }}
                    />
                  </div>
                  {formData.preferred_date && (
                    <p className="text-[#D4AF37] font-manrope text-xs mt-3" data-testid="selected-date-label">
                      Selected: {new Date(formData.preferred_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-3 block">
                    <Clock size={12} className="inline mr-2" />Preferred Time
                  </label>
                  <div className="space-y-4" data-testid="time-options">
                    {timeGroups.map(group => (
                      <div key={group.label}>
                        <p className="text-white/25 font-manrope text-[10px] uppercase tracking-widest mb-2">{group.label}</p>
                        <div className="flex flex-wrap gap-2">
                          {group.slots.map(time => {
                            const isBooked = bookedSlots.includes(time);
                            return (
                              <button
                                key={time}
                                type="button"
                                disabled={isBooked}
                                onClick={() => update('preferred_time', time)}
                                className={`
                                  px-3.5 py-2 rounded-sm font-manrope text-xs transition-all duration-300
                                  ${isBooked
                                    ? 'bg-white/5 text-white/15 cursor-not-allowed line-through'
                                    : formData.preferred_time === time
                                      ? 'bg-[#D4AF37] text-black font-bold'
                                      : 'bg-[#1a1a1a] border border-white/10 text-white/50 hover:border-[#D4AF37]/30'
                                  }
                                `}
                                data-testid={`time-${time.replace(/[\s:]/g, '-')}`}
                              >
                                {time}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Message (Optional)</label>
                <textarea
                  value={formData.message}
                  onChange={e => update('message', e.target.value)}
                  rows={3}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors resize-none"
                  placeholder="Tell us about your project..."
                  data-testid="booking-message-input"
                />
              </div>

              {/* Travel fee summary */}
              {quote && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-[#D4AF37]/15 to-[#D4AF37]/5 border border-[#D4AF37]/40 rounded-sm p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                  data-testid="travel-fee-banner"
                >
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-manrope font-semibold mb-1">
                      {quote.custom_quote ? 'Custom Travel Quote Required' : 'Consultation Travel Fee'}
                    </p>
                    <p className="text-white/50 font-manrope text-xs">
                      {quote.region}
                      {quote.distance_km ? ` · ~${Math.round(quote.distance_km)} km round trip` : ''}
                    </p>
                  </div>
                  <span className="font-playfair text-3xl text-[#D4AF37]" data-testid="travel-fee-amount">
                    {quote.custom_quote ? "We'll Contact You" : `$${quote.fee}`}
                  </span>
                </motion.div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-black hover:bg-[#C5A059] uppercase tracking-widest px-8 py-4 rounded-sm font-manrope font-bold text-sm transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="submit-booking-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : quote?.custom_quote ? (
                  <><Send size={16} /> Request Custom Quote</>
                ) : quote?.fee ? (
                  <><CreditCard size={16} /> Book & Pay ${quote.fee}</>
                ) : (
                  <><Send size={16} /> Book Consultation</>
                )}
              </button>
              {!canSubmit && !loading && (
                <p className="text-white/25 font-manrope text-xs text-center -mt-4" data-testid="form-incomplete-hint">
                  {!isComplete
                    ? 'Fill in your details, address, date and time to unlock booking.'
                    : quoteLoading
                      ? 'Calculating your travel fee...'
                      : 'Enter a valid address (or select your region) to see your travel fee.'}
                </p>
              )}
            </motion.form>
          )}
        </div>
      </section>
    </div>
  );
}
